import { useRef } from "react";
import { IconChevronRight } from "@tabler/icons-react";
import { SelectOption } from "./quick-animation-catalog";
import { AnimationStyle, CameraMovement } from "@repo/api/animated-image";
import { Badge } from "@/lib/components/Badge";
import Image from "@/lib/components/Image";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";
import { cn } from "@/lib/utils";

type QuickAnimationsListProps<T extends CameraMovement | AnimationStyle> = {
  options: SelectOption<T>[];
  onSelect: (value: SelectOption<T>["value"]) => void;
  variant?: "default" | "compact"; // or whatever variants you need
  nextStepExplanation?: string;
  hasNextStep?: boolean;
};

export function CameraMovementList({
  options,
  onSelect,
  variant = "default",
  nextStepExplanation,
  hasNextStep,
}: QuickAnimationsListProps<CameraMovement>) {
  return (
    <QuickAnimationsList
      options={options}
      onSelect={onSelect}
      variant={variant}
      nextStepExplanation={nextStepExplanation}
      hasNextStep={hasNextStep}
    />
  );
}

export function StyleList({
  options,
  onSelect,
  nextStepExplanation,
  hasNextStep,
}: QuickAnimationsListProps<AnimationStyle>) {
  return (
    <QuickAnimationsList
      options={options}
      onSelect={onSelect}
      nextStepExplanation={nextStepExplanation}
      hasNextStep={hasNextStep}
    />
  );
}

function QuickAnimationsList<T extends CameraMovement | AnimationStyle>({
  options,
  onSelect,
  variant,
  nextStepExplanation,
  hasNextStep,
}: QuickAnimationsListProps<T>) {
  const isCompact = variant === "compact";

  return (
    <div
      className={cn(
        "flex h-full flex-1 flex-col gap-4 self-stretch overflow-y-auto px-4",
        isCompact ? "px-8 py-6" : "p-4",
      )}
    >
      <div className="grid grid-cols-3 gap-2">
        {options.map((option) => (
          <QuickAnimationListItem
            key={option.value}
            option={option}
            onSelect={onSelect}
            isCompact={isCompact}
            nextStepExplanation={nextStepExplanation}
            hasNextStep={hasNextStep}
          />
        ))}
      </div>
    </div>
  );
}

type QuickAnimationListItemProps<T extends CameraMovement | AnimationStyle> = {
  option: SelectOption<T>;
  onSelect: (value: SelectOption<T>["value"]) => void;
  size?: "large" | "base";
  isCompact?: boolean;
  nextStepExplanation?: string;
  hasNextStep?: boolean;
};

function QuickAnimationListItem<T extends CameraMovement | AnimationStyle>({
  option,
  onSelect,
  size = "base",
  isCompact = false,
  nextStepExplanation,
  hasNextStep,
}: QuickAnimationListItemProps<T>) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const Icon = option.icon;
  const videoPreview = option.videoPreview;

  const handleActivate = () => {
    if (!videoPreview || !videoRef.current) return;
    videoRef.current.currentTime = 0;
    void videoRef.current.play().catch(() => {});
  };

  const handleDeactivate = () => {
    if (!videoPreview || !videoRef.current) return;
    videoRef.current.pause();
    videoRef.current.currentTime = 0;
  };

  const button = (
    <button
      ref={buttonRef}
      className={cn(
        "group relative w-full cursor-pointer overflow-hidden rounded-md select-none focus-visible:outline-2",
        isCompact ? "h-full min-h-36" : "h-36",
      )}
      onClick={() => onSelect(option.value)}
      onMouseEnter={handleActivate}
      onMouseLeave={handleDeactivate}
      onFocus={handleActivate}
      onBlur={handleDeactivate}
    >
      <Image
        src={`/animations/${option.value}.jpg`}
        alt={option.label}
        className="h-full w-full object-cover"
      />
      {videoPreview && (
        <video
          ref={videoRef}
          preload="none"
          src={`/animations/${option.value}.mp4`}
          muted
          loop
          playsInline
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
      <div
        className={cn(
          "absolute inset-0 flex flex-col items-center justify-center bg-black/50 text-white transition-all duration-300",
          "group-hover:opacity-0 group-focus:opacity-0",
        )}
      >
        <Icon
          className={cn("mb-1", size === "large" ? "h-6.5 w-6.5" : "h-5 w-5")}
          strokeWidth={2}
        />
        <span
          className={cn(
            "font-semibold",
            size === "large" ? "text-base" : "text-sm",
          )}
        >
          {option.label}
        </span>
      </div>
      {option.isPopular && (
        <Badge
          variant="gradientSubtle"
          size="xs"
          className="absolute top-1.5 right-1.5 transition-opacity duration-300 group-hover:opacity-0 group-focus:opacity-0"
        >
          Popular
        </Badge>
      )}
      {hasNextStep && (
        <IconChevronRight
          className="absolute right-2 bottom-2.5 h-4.5 w-4.5 text-white/80 transition-opacity duration-300 group-hover:opacity-0 group-focus:opacity-0"
          stroke={1.5}
        />
      )}
    </button>
  );

  if (!option.explanation) {
    return button;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="bottom">
        <div className="flex flex-col items-center gap-1 px-3 py-2 text-center">
          <span className="text-[13px] font-semibold">
            {option.explanation}
          </span>
          {nextStepExplanation && (
            <span className="text-xs font-normal">{nextStepExplanation}</span>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
